/**
 * Application route constants — MineMiner SPA build.
 *
 * sprint-104 (epic-013 Sprint B, step 2d / S5). Forked from `Frontend/`'s
 * routes.ts and slimmed to the MineMiner-relevant routes:
 *
 *   - `MINEMINER_ROUTES` — full carry (all /MineMiner/* paths unchanged).
 *   - `PUBLIC_ROUTES` — only login + the public demo-scheduling landing.
 *   - `UTILITY_ROUTES` — access-denied + 404.
 *
 * No CUSTOMER_ROUTES / NONPROFIT_ROUTES / ADMIN_ROUTES / MARKETING_ROUTES —
 * those live on the core MentionedMe SPA at mentionedme.com.
 */

/**
 * Public routes (no authentication required).
 */
export const PUBLIC_ROUTES = {
  LOGIN: '/login',
  // Sprint 098 (epic-011 Sprint D) MineMiner demo-scheduling landing page.
  // Public, no auth — the link token is the only trust boundary.
  SCHEDULE_DEMO: '/schedule-demo/:token',
  // sprint-107 S1.5 (epic-013 Sprint D — Stream A item A2) carved
  // forgot-password / reset-password flow on the carved host. Reset page
  // reads `?uid=&token=` from the query string (sprint-281; matches the link shape
  // built by MineMinerForgotPasswordCommandHandler).
  FORGOT_PASSWORD: '/forgot-password',
  RESET_PASSWORD: '/reset-password',
  // sprint-108 S1 — anonymous accept-invite landing. Reads `?uid=&token=` (sprint-281)
  // (matches the link MineMinerInviteUserCommandHandler builds against
  // MineMiner:RepBaseUrl). Page mirrors ResetPasswordPage shape.
  ACCEPT_INVITE: '/accept-invite',
  // sprint-279 S003 (F23 slice 3) — an outside contact's step link. The token in the path is the
  // only key; staticwebapp.config.json sends no-referrer / no-store / noindex for /x/*.
  EXTERNAL_STEP: '/x/:token',
} as const;

/**
 * Utility routes (no layout).
 */
export const UTILITY_ROUTES = {
  ACCESS_DENIED: '/access-denied',
  NOT_FOUND: '/404',
} as const;

/**
 * Signed-in routes: any authenticated user, whatever the role (ProtectedRoute, no layout).
 */
export const SIGNED_IN_ROUTES = {
  // Sync Mine Safety LinkedIn drafts shared for review (committed artifact, see
  // pages/SyncLinkedIn/syncLinkedInPosts.ts). Path per stakeholder request.
  SYNC_LINKEDIN_DRAFTS: '/SyncMineSafety/LinkedIn',
} as const;

/**
 * Internal-admin routes: MineMinerAdmin only (MineMinerAdminRoute, no layout).
 */
export const ADMIN_ONLY_ROUTES = {
  // MyMineMiner LinkedIn drafts shared for internal review (committed artifact, see
  // pages/MyMineMinerLinkedIn/myMineMinerLinkedInPosts.ts). Not for reps, clients or Sync users.
  MYMINEMINER_LINKEDIN_DRAFTS: '/MyMineMiner/LinkedIn',
} as const;

/**
 * MineMiner routes (MineMinerRep / MineMinerAdmin role required).
 */
export const MINEMINER_ROUTES = {
  BASE: '/MineMiner',
  // sprint-173 (TECH_DEBT #127): the Mines list moved off the apex onto its own path. `BASE`
  // (`/MineMiner`) now redirects to `ACCOUNT_INTELLIGENCE` (the default post-login landing);
  // the list lives at `MINES` and a single mine at `MINE_DETAIL` (`/MineMiner/mines/:mineId`).
  MINES: '/MineMiner/mines',
  LEADS_NEW_MINE: '/MineMiner/leads/new-mine',
  LEADS_RECENT_CITATION: '/MineMiner/leads/recent-citation',
  LEADS_IMMINENT: '/MineMiner/leads/imminent',
  LEAD_DETAIL: '/MineMiner/leads/:signal/:leadId',
  MINE_DETAIL: '/MineMiner/mines/:mineId',
  // Sprint-233 — single-inspection drill-down (EventNo-keyed) off the mine detail
  // inspections section.
  MINE_INSPECTION_DETAIL: '/MineMiner/mines/:mineId/inspections/:eventNo',
  VIOLATIONS: '/MineMiner/violations',
  ADMIN: '/MineMiner/admin',
  OPERATORS_LIST: '/MineMiner/operators',
  OPERATOR_DETAIL: '/MineMiner/operators/:operatorId',
  CONTROLLERS_LIST: '/MineMiner/controllers',
  CONTROLLER_DETAIL: '/MineMiner/controllers/:controllerId',
  CONTRACTORS_LIST: '/MineMiner/contractors',
  CONTRACTOR_DETAIL: '/MineMiner/contractors/:contractorId',
  CONTACTS_LIST: '/MineMiner/contacts',
  CONTACT_DETAIL: '/MineMiner/contacts/:contactId',
  // sprint-125 (epic-019 Sprint D) — Companies "Coming Soon" stub. Gives the
  // epic-017 Account/Company layer a nav home without the full list/detail UI
  // (TECH_DEBT #129). ADR-001.
  COMPANIES_LIST: '/MineMiner/companies',
  // sprint-120 (epic-017 Sprint D) — mobile booth-capture mode. Standalone form
  // with an in-page active-event pin; the `:eventId` variant deep-links from an
  // Event detail page with that event pre-pinned.
  CAPTURE: '/MineMiner/capture',
  CAPTURE_EVENT: '/MineMiner/capture/:eventId',
  EVENT_DETAIL: '/MineMiner/events/:eventId',
  EVENTS_LIST: '/MineMiner/events',
  EVENT_EXHIBITORS: '/MineMiner/events/:eventId/exhibitors',
  LISTS_LIST: '/MineMiner/lists',
  LIST_DETAIL: '/MineMiner/lists/:listId',
  WORK_QUEUE: '/MineMiner/work-queue',
  CAMPAIGNS_LIST: '/MineMiner/campaigns',
  CAMPAIGN_DETAIL: '/MineMiner/campaigns/:campaignId',
  // sprint-110 (epic-015 Sprint B) — user-authored email templates editor.
  // Detail route uses the sentinel value 'new' for create mode; the editor
  // page checks `emailTemplateId === 'new'` to switch its form into create
  // shape. Single registered route covers both edit + create.
  EMAIL_TEMPLATES_LIST: '/MineMiner/email-templates',
  EMAIL_TEMPLATE_DETAIL: '/MineMiner/email-templates/:emailTemplateId',
  REPORTS: '/MineMiner/reports',
  // sprint-147 — on-site MSHA 24-month violations analytics page. Rep-level
  // (NOT admin-gated): visible to every MineMinerRep. Reads the latest weekly
  // snapshot and renders the four analytical lenses + per-cut CSV downloads.
  ANALYTICS: '/MineMiner/analytics',
  // sprint-152 (epic-020) — Sync offering ↔ CFR mapping review. Rep-level. Shows
  // the SyncOfferingCatalog definitions (the single source of truth the analytics
  // CASE is generated from) enriched with observed counts, plus a "Propose a
  // change" capture (intent only — no recompute).
  OFFERINGS: '/MineMiner/offerings',
  // epic-025 Sprint D — Account Intelligence: the recurring executive briefing that
  // generalizes /Elko2026 into two ranked views (top accounts by client value +
  // most-urgent outreach). Rep-level, same as Analytics. A thin renderer over the
  // committed artifact pages/AccountIntelligence/accountIntelligenceBriefing.ts
  // (ADR-004: manual weekly Claude-driven refresh, no server-side automation in v1).
  ACCOUNT_INTELLIGENCE: '/MineMiner/account-intelligence',
  AUDIT: '/MineMiner/audit',
  // sprint-108 S2 — auth-gated change-password for any signed-in MineMiner
  // user (Rep or Admin). Entry point is a Menu.Item in the Header avatar
  // dropdown. Lives under the role-gated /MineMiner/* tree so it inherits
  // MineMinerLayout (sidebar + header).
  CHANGE_PASSWORD: '/MineMiner/change-password',
  // sprint-130 — Elko 2026 rep field briefing. Root-level path (NOT under
  // /MineMiner/) but registered inside the MineMinerRoute + MineMinerLayout tree,
  // so it inherits the rep auth-gate and app chrome. Curated static content; the
  // briefing IS the deliverable (see pages/Elko2026/elko2026Briefing.ts).
  ELKO_2026: '/Elko2026',
  // Tata Chemicals Mine fatality briefing (July 29, 2026, MSHA ID 4800155).
  // Root-level path per stakeholder request, same auth-gated Elko2026 shape; the
  // briefing IS the deliverable (see pages/TataBriefing/tataFatalityBriefing.ts).
  TATA_BRIEFING: '/Tata',
  // sprint-235 (epic-025) — Hecla Mining Company account overview (MSHA controller M00089).
  // Root-level path per stakeholder request, same auth-gated Elko2026/Tata shape; the briefing
  // IS the deliverable (see pages/HeclaBriefing/heclaBriefing.ts).
  // ⚠️ NOT a fatality briefing — /Tata is organised around an incident and that shape does not
  // transfer. This answers "what do I need to know before the meeting".
  HECLA_BRIEFING: '/Hecla',
  // sprint-250 (epic-039 Canary increment F) — the five curated account overviews on ONE shared
  // template (charter D8), replacing the per-account bespoke page the four routes above each grew.
  // These two are the STAFF door: same MineMinerRoute + MineMinerLayout gate as every route in this
  // block. Organization users reach the identical component through ORGANIZATION_ROUTES below —
  // two doors, one page, because the organization gate deliberately refuses staff and the staff
  // gate deliberately refuses organization members (epic-039/DR-002 clause 2).
  ACCOUNT_OVERVIEWS: '/MineMiner/account-overviews',
  ACCOUNT_OVERVIEW_DETAIL: '/MineMiner/account-overviews/:accountKey',
} as const;

/**
 * Client-area routes (sprint-217 — epic-036 Phase 1 increment 1D, DR-008).
 *
 * The customer-facing surface: a mine operator's own inspection events, evidence and MSHA
 * violations. Gated by `MineClientRoute` on the three mine-scoped client roles, and chromed
 * by `MineClientLayout` — a different guard and a different layout from the `/MineMiner/*`
 * staff tree above.
 *
 * The `/client` prefix is deliberate and worth keeping: a path is the cheapest possible
 * answer to "whose data am I looking at", and support reads these out of URLs in tickets.
 *
 * ⚠️ The keys carry a `CLIENT_` prefix because `ROUTES` below is a FLAT spread of all four
 * groups. Naming these `BASE` and `MINES` would have silently overridden
 * `MINEMINER_ROUTES.BASE` and `.MINES` — the staff Mines list would have started resolving
 * to `/client/mines`, with no type error and no test failure to catch it.
 */
export const CLIENT_ROUTES = {
  CLIENT_BASE: '/client',
  /** The mines this user belongs to — the client area's landing surface. */
  CLIENT_MINES: '/client/mines',

  // sprint-252 (epic-036 Phase 4 increment 4H, DR-041) — the Legal portfolio.
  //
  // 🔴 Deliberately NO :mineId — the route is SELF-SCOPED, like the mines list: the server
  // resolves the caller's own active Legal grants (PortfolioMembershipRequirement) and reads
  // nothing outside them, so there is no route value for anyone to tamper with. Keeping the
  // path shape aligned with the API (`portfolio/…`, no mine segment) keeps the thing on
  // screen and the thing the server authorised the same string — the same argument the
  // per-mine block below makes in the other direction.
  /** Every mine the signed-in Legal user holds a grant at, read together (DR-041). */
  CLIENT_PORTFOLIO: '/client/portfolio',

  // sprint-217 goal 3 — the per-mine surfaces.
  //
  // 🔴 The mine id is a PATH SEGMENT, not a query parameter, and that is a security property
  // rather than a URL-style preference: MineMembershipRequirement reads the `mineId` ROUTE
  // value and resolves an active membership for it before the endpoint answers. Keeping the
  // SPA's shape aligned with the API's means the thing the user is looking at and the thing
  // the server authorised are the same string.
  /** One mine — MSHA detail, with the violations and inspections tabs hanging off it. */
  CLIENT_MINE: '/client/mines/:mineId',
  /** MSHA violations issued at this mine. */
  CLIENT_MINE_VIOLATIONS: '/client/mines/:mineId/violations',
  /**
   * One citation in full — MSHA's published record and the operator's response as two facts
   * (sprint-229, 4A/N3/U2 — the first §5.9 surface). Keyed by the violation NUMBER, the string
   * printed on the paperwork, for the reason the API's route is: it is the one identifier the
   * operator actually holds, and it reaches pre-ingest operator-entered citations that have no
   * ingest row id at all.
   */
  CLIENT_MINE_VIOLATION_DETAIL: '/client/mines/:mineId/violations/:violationNo',
  /** Inspection events at this mine, and the evidence captured under them. */
  CLIENT_MINE_INSPECTIONS: '/client/mines/:mineId/inspections',
  /**
   * sprint-269 (S005) — the live inspection-day triage surface: what the Supervisor's phone is
   * capturing RIGHT NOW, grouped by stop with urgent items first, and the assign act one click
   * from a captured item (epic-042/D2).
   */
  CLIENT_MINE_INSPECTION_TRIAGE: '/client/mines/:mineId/inspection-triage',

  // goals 4b+5 — the ADR-005 verify gate.
  //
  // A route of its own rather than a modal over the inspections page, for three reasons that
  // all point the same way: 21 fields do not fit the 5-column panel the attachment list lives
  // in; a half-finished check survives a refresh here and would not in a modal; and support can
  // be sent straight to the form someone is stuck on. The attachment id is a path segment for
  // the same reason `mineId` is — the API names it in the route, and the server resolves it
  // THROUGH the mine before reading a byte.
  /** Check what the OCR read off one 7000-3, correct it, and file it. */
  CLIENT_MINE_CONFIRM_7000_3: '/client/mines/:mineId/7000-3/:attachmentId/confirm',

  // sprint-227 (epic-036 Phase 3 increment 3F) — the abatement surface.
  //
  // 🔴 THE `CLIENT_` PREFIX IS NOT A NAMING PREFERENCE HERE, IT IS THE WHOLE SAFETY PROPERTY.
  // `ROUTES` below is a FLAT spread of all four groups, and `MINEMINER_ROUTES` already owns keys
  // in this shape. A key named `TASKS` or `NOTIFICATIONS` would silently override the staff one:
  // the staff surface would start resolving to a `/client/...` path, with no type error and no
  // failing test — the trap the group's header warns about, one increment later and with more
  // keys to trip on.
  //
  // The mine id stays a PATH SEGMENT for the reason the block above gives, and the task id joins
  // it for the same reason: the server resolves the task THROUGH the mine before it answers, so
  // keeping the SPA's shape aligned with the API's means the thing on screen and the thing that
  // was authorised are the same two strings.

  /** The mine's abatement work — what was assigned, to whom, and where it stands. */
  CLIENT_MINE_TASKS: '/client/mines/:mineId/tasks',

  /**
   * One task in full: the deadline block, the evidence, the pushback chain, and the actions.
   *
   * A route of its own rather than a modal over the list, for the reasons
   * `CLIENT_MINE_CONFIRM_7000_3` gives and one more that is specific to this surface: a
   * Supervisor completing a task uploads a file first, and a half-finished upload must survive a
   * refresh. Support can also be sent straight to the task somebody is stuck on.
   */
  CLIENT_MINE_TASK: '/client/mines/:mineId/tasks/:abatementTaskId',

  /**
   * The review queue — completion reports filed and awaiting the Admin's judgment (sprint-230,
   * 4B). A tab in the mine shell: the queue is a fact about THIS mine, and it is also 4D's
   * future fail-closed destination, so it earns a place of its own rather than a filter control
   * on the tasks tab. Decisions happen on the task detail page, where the evidence is.
   */
  CLIENT_MINE_REVIEW_QUEUE: '/client/mines/:mineId/review-queue',

  /**
   * The reconciliation queue — what the product asserted about this mine's citations, against
   * what MSHA's weekly feed has published (sprint-231, 4C — SC-5's browser surface). A tab in
   * the mine shell beside the review queue: the queue holds the mine's own claims waiting on an
   * Admin; this holds the layer above, and its Mismatch rows are the durable §5.8 to-dos.
   */
  CLIENT_MINE_RECONCILIATION: '/client/mines/:mineId/reconciliation',

  /**
   * The evidence-integrity register — files whose verification failed, plus everything under an
   * active legal hold (sprint-244, 4E-2 Goal 4; scoped to 4E-1 by charter §4.6 and never built).
   * A tab in the mine shell beside the other two registers, because it is the same question one
   * layer down again: the review queue holds claims waiting on an Admin, reconciliation holds what
   * MSHA published against them, and this holds whether the evidence underneath either is still
   * what was filed.
   */
  CLIENT_MINE_EVIDENCE_INTEGRITY: '/client/mines/:mineId/evidence-integrity',

  /**
   * Who this mine's citations were issued by, and how to reach them (sprint-244, 4E-2 Goal 7).
   * A tab in the mine shell because addressing an envelope is the act every other register leads
   * to: the review queue holds what waits on an Admin, reconciliation what MSHA published, evidence
   * integrity whether the files still stand — and this holds who the answer gets sent to.
   *
   * ⚠️ Named for the people, not for "correspondence". 4E-2 ships no send and no packet lifecycle;
   * a tab promising correspondence would name something that does not exist yet.
   */
  CLIENT_MINE_INSPECTORS: '/client/mines/:mineId/inspectors',

  /**
   * Correspondence (sprint-244/S005, 4E-2 Goal 7 part 2; the send and the reply sprint-249, 4F):
   * the mine's packets — drafted, sent, and with MSHA's reply recorded — and one packet's page,
   * which shows the packet's own state and its feed watch as two facts (§5.9).
   */
  CLIENT_MINE_CORRESPONDENCE: '/client/mines/:mineId/correspondence',
  CLIENT_MINE_CORRESPONDENCE_PACKET: '/client/mines/:mineId/correspondence/:packetId',

  /** This user's notifications at this mine, with per-channel delivery state (increment 3C). */
  CLIENT_MINE_NOTIFICATIONS: '/client/mines/:mineId/notifications',

  /**
   * The mine's internal-deadline ladder — view for every member, set for Admins (sprint-229,
   * 4A/F6). A tab in the mine shell rather than a page of its own: the policy is a fact about
   * THIS mine, and the shell keeps the mine's identity on screen while somebody changes a number
   * that recomputes every task's target here.
   */
  CLIENT_MINE_DEADLINE_POLICY: '/client/mines/:mineId/deadline-policy',
} as const;

/**
 * Organization-area routes — the THIRD tenancy lane (sprint-239, epic-039 Canary increment A).
 *
 * 🔴 THE `ORG_` PREFIX IS NOT A NAMING PREFERENCE, IT IS THE SAFETY PROPERTY the `CLIENT_`
 * block above spells out: `ROUTES` is a FLAT spread of every group, so a key named `HOME` or
 * `CANARY` here would silently override a same-named key elsewhere, with no type error and no
 * failing test.
 *
 * 🔴 The organization id is a PATH SEGMENT, for the same reason `mineId` is one:
 * `OrganizationMembershipRequirement` reads the `organizationId` ROUTE value and resolves an
 * active membership before the endpoint answers — and it DECLINES a request that names no
 * organization. Keeping the SPA's shape aligned with the API's means the thing on screen and
 * the thing the server authorised are the same string.
 */
export const ORGANIZATION_ROUTES = {
  /** The organizations this user belongs to — the area's landing surface. */
  ORG_BASE: '/MineMiner/org',

  /** One organization: who you are here, and what it is licensed for. */
  ORG_HOME: '/MineMiner/org/:organizationId',

  /**
   * The Canary surface for one organization. Gated server-side on membership AND an active
   * Canary license — increment A ships that gate; watch lists and the change feed are B and C.
   */
  ORG_CANARY: '/MineMiner/org/:organizationId/canary',

  /**
   * The curated account overviews for one organization (sprint-250, increment F). Same component
   * the staff route renders; the organization id stays a PATH SEGMENT for the reason given above,
   * so the client gate and the server's own membership check read the same string.
   *
   * 🔴 Gated on the `CuratedBriefings` license, not the Canary one. An organization can be licensed
   * for change detection without being licensed for the curated reads, and vice versa.
   */
  ORG_BRIEFINGS: '/MineMiner/org/:organizationId/briefings',
  ORG_BRIEFING_DETAIL: '/MineMiner/org/:organizationId/briefings/:accountKey',
} as const;

/**
 * Elko Mining Expo event id — the active tradeshow.
 *
 * sprint-124 (epic-019 Sprint C). Tradeshow wiring referenced this event from the
 * brand logo/wordmark (`Header`) and the locked booth Capture deep-links.
 * sprint-173 (TECH_DEBT #127): the post-login redirect was reverted off this event;
 * the constant stays as the canonical Elko event id reference.
 */
export const ELKO_EVENT_ID = '21f0de82-4b2c-4061-87f8-911753f5f4f3';

/**
 * All routes combined.
 */
export const ROUTES = {
  ...PUBLIC_ROUTES,
  ...UTILITY_ROUTES,
  ...MINEMINER_ROUTES,
  ...CLIENT_ROUTES,
  ...ORGANIZATION_ROUTES,
} as const;

/**
 * Helper to build a route with parameters substituted in.
 */
export function buildRoute(route: string, params: Record<string, string>): string {
  let builtRoute = route;

  Object.entries(params).forEach(([key, value]) => {
    builtRoute = builtRoute.replace(`:${key}`, value);
  });

  return builtRoute;
}
